import { Modal } from '@mui/material';
import React, { useState } from 'react';
import { AppButton } from '../AppButton/AppButton';
import { X } from 'react-feather';
import { COLORS } from '../../../utils/colors';
import RequestedJobList from './RequestedJobList';
import { on } from 'events';
import { RequestedSupplierJobApiResponse, SupplierJobApiResponse } from '@dill/dill-shared';
import RequestedJobView from './RequestedJobView';
import { updateSupplierJob } from '../../../redux/services/supplierJobsService';
import { useAppDispatch, useAppSelector } from '../../../redux/store';
import { openDillToast } from '../../../utils/helpers';
import {
  getRequestedSupplierJobs,
  updateRequestedSupplierJob,
} from '../../../redux/services/requestJobInformationService';
import { updateSupplierBuyer } from '../../../redux/services/supplierBuyersService';

enum ModalState {
  JOB_LIST,
  JOB_VIEW,
}

const AppRequestedJobsModal = ({
  open,
  handleClose,
  requestedSupplierJobs,
  onEditSupplierJob,
  onReload,
  onPreviewAttachment,
  onDownloadAttachment,
}: {
  open: boolean;
  handleClose: () => void;
  requestedSupplierJobs: RequestedSupplierJobApiResponse[];
  onEditSupplierJob: (data: SupplierJobApiResponse, id: string) => void;
  onReload: () => void;
  onPreviewAttachment: (url: string, fileType: string) => void;
  onDownloadAttachment: (url: string, name: string) => void;
}) => {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const currentSupplier = useAppSelector((state) => state.auth.currentSupplier);
  const [modalState, setModalState] = useState<ModalState>(ModalState.JOB_LIST);
  const [selectedJob, setSelectedJob] = useState<RequestedSupplierJobApiResponse>(
    {} as RequestedSupplierJobApiResponse
  );
  const [accepting, setAccepting] = useState<boolean>(false);
  const [declining, setDeclining] = useState<boolean>(false);

  const handleCloseModal = () => {
    if (modalState === ModalState.JOB_LIST) {
      handleClose();
    }
    setModalState(ModalState.JOB_LIST);
    setSelectedJob({} as RequestedSupplierJobApiResponse);
  };

  const handleSelectJob = (data: RequestedSupplierJobApiResponse) => {
    setSelectedJob(data);
    setModalState(ModalState.JOB_VIEW);
  };
  const handleAcceptJob = async (data: RequestedSupplierJobApiResponse) => {
    setAccepting(true);

    if (selectedJob?.buyerId === '') {
      const acceptSupplierJob = await dispatch(
        updateRequestedSupplierJob({
          supplierId: currentSupplier?.id || '',
          requestedSupplierJobId: data.id,
          requestedSupplierJobDetails: { status: 'approved', buyerId: data.buyerId },
        })
      );
      if (acceptSupplierJob.type === 'requestJobInformation/updateRequestedSupplierJob/fulfilled') {
        await dispatch(getRequestedSupplierJobs({ supplierId: currentSupplier?.id || '' }));
        setAccepting(false);
        onReload();
        openDillToast({ message: 'Successfully accepted job', type: 'SUCCESS' });
        handleCloseModal();
      } else {
        setAccepting(false);
        openDillToast({ type: 'ERROR', message: 'Failed to accept job' });
      }
    } else {
      let moreDetails = {};
      if (data.jobState === 'AZ') {
        moreDetails = {
          ...moreDetails,
          jobCounty: data.jobCounty,
          jobLegalDescription: data.jobLegalDescription,
        };
      }
      if (data.jobInfoSheetAttachments) {
        moreDetails = {
          ...moreDetails,
          jobInfoSheetAttachments: data.jobInfoSheetAttachments,
        };
      }
      const supplierJobData = {
        name: data.name,
        jobNumber: data.jobNumber,
        jobAddress: data.jobAddress,
        jobCity: data.jobCity,
        jobState: data.jobState,
        jobZipCode: data.jobZipCode,
        owners: data.owners || [],
        GCs: data.GCs || [],
        lenders: data.lenders || [],
        jobType: data.jobType,
        jobSector: data.jobSector,
        customerType: data.customerType,
        jobFurnishings: data.jobFurnishings,
        hasNoLender: data.hasNoLender,
        estimatedMaterialAmount: data.estimatedMaterialAmount,
        apn: data.apn,
        buyerId: data.buyerId,
        isJobBonded: data.isJobBonded ?? false,
        sureties: data.sureties || [],
        isGCUnknown: false,
        isOwnerUnknown: false,
        isLenderUnknown: false,
        isSurityUnknown: false,
        ...moreDetails,
      };
      const updateSupplierJobResponse = await dispatch(
        updateSupplierJob({
          supplierId: currentSupplier?.id || '',
          supplierJobId: data.supplierJobId,
          supplierJobDetails: supplierJobData,
        })
      );
      if (updateSupplierJobResponse.type === 'supplierJobs/updateSupplierJob/fulfilled') {
        await dispatch(
          updateRequestedSupplierJob({
            supplierId: currentSupplier?.id || '',
            requestedSupplierJobId: data.id,
            requestedSupplierJobDetails: { status: 'approved' },
          })
        );
        await dispatch(getRequestedSupplierJobs({ supplierId: currentSupplier?.id || '' }));
        if (data?.customerAddress?.address !== undefined) {
          await dispatch(
            updateSupplierBuyer({
              supplierId: currentSupplier?.id || '',
              supplierBuyerId: data?.buyerId || '',
              supplierBuyerDetails: {
                address: data.customerAddress?.address || '',
                city: data.customerAddress?.city || '',
                state: data.customerAddress?.state || '',
                zipCode: data.customerAddress?.zipCode || '',
              },
            })
          );
          onReload();
        }
        setAccepting(false);
        openDillToast({ message: 'Successfully accepted job', type: 'SUCCESS' });
        handleCloseModal();
      } else {
        setAccepting(false);
        openDillToast({ type: 'ERROR', message: 'Failed to accept job' });
      }
    }
  };

  const handleDeclineJob = async (data: RequestedSupplierJobApiResponse) => {
    setDeclining(true);
    const declineSupplierJob = await dispatch(
      updateRequestedSupplierJob({
        supplierId: currentSupplier?.id || '',
        requestedSupplierJobId: data.id,
        requestedSupplierJobDetails: { status: 'declined' },
      })
    );
    if (declineSupplierJob.type === 'requestJobInformation/updateRequestedSupplierJob/fulfilled') {
      await dispatch(getRequestedSupplierJobs({ supplierId: currentSupplier?.id || '' }));
      setDeclining(false);
      openDillToast({ message: 'Successfully declined job', type: 'SUCCESS' });
      handleCloseModal();
    } else {
      setDeclining(false);
      openDillToast({ type: 'ERROR', message: 'Failed to decline job' });
    }
  };

  const handleEditJob = async (data: RequestedSupplierJobApiResponse) => {
    let moreDetails = {};
    if (data.jobState === 'AZ') {
      moreDetails = {
        ...moreDetails,
        jobCounty: data.jobCounty,
        jobLegalDescription: data.jobLegalDescription,
      };
    }
    if (data.jobInfoSheetAttachments) {
      moreDetails = {
        ...moreDetails,
        jobInfoSheetAttachments: data.jobInfoSheetAttachments,
      };
    }
    const supplierJobData: SupplierJobApiResponse = {
      name: data.name,
      jobNumber: data.jobNumber,
      jobAddress: data.jobAddress,
      jobCity: data.jobCity,
      jobState: data.jobState,
      jobZipCode: data.jobZipCode,
      owners: data.owners,
      GCs: data.GCs,
      lenders: data.lenders,
      jobType: data.jobType,
      jobSector: data.jobSector,
      customerType: data.customerType,
      jobFurnishings: data.jobFurnishings,
      hasNoLender: data.hasNoLender,
      estimatedMaterialAmount: data.estimatedMaterialAmount,
      apn: data.apn,
      id: data.supplierJobId,
      supplierId: data.supplierId,
      buyerId: data.buyerId,
      isJobBonded: data.isJobBonded ?? false,
      sureties: data.sureties ?? [],
      ...moreDetails,
    };
    onEditSupplierJob(supplierJobData, data.id);
    handleCloseModal();
  };
  return (
    <Modal
      open={open}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description">
      <>
        {modalState === ModalState.JOB_LIST && (
          <RequestedJobList
            onClose={handleCloseModal}
            requestedSupplierJobs={requestedSupplierJobs}
            onSelectJob={handleSelectJob}
          />
        )}
        {modalState === ModalState.JOB_VIEW && (
          <RequestedJobView
            onClose={handleCloseModal}
            requestedSupplierJob={selectedJob}
            onAcceptSupplierJob={handleAcceptJob}
            onEditSupplierJob={handleEditJob}
            accepting={accepting}
            onDeclineSupplierJob={handleDeclineJob}
            declining={declining}
            onPreviewAttachment={onPreviewAttachment}
            onDownloadAttachment={onDownloadAttachment}
          />
        )}
      </>
    </Modal>
  );
};

export default AppRequestedJobsModal;
